import { useState, useEffect } from 'react'
import { useQuery } from '@apollo/client'
import {
  ArrowLeftIcon,
  ArrowRightIcon,
  ForwardIcon,
  UserGroupIcon,
  CheckCircleIcon,
  LockClosedIcon,
} from '@heroicons/react/24/outline'
import { GET_STAFF_CONSUMPTION_REPORT } from '../../../../graphql/reports-queries'
import { usePermissions } from '../../../../hooks/usePermissions'
import { PERMISSIONS } from '../../../../lib/permissions'

interface StaffConsumptionStepProps {
  date: string
  onNext: () => void
  onBack: () => void
  isFirst: boolean
}

function formatEuro(cents: number) {
  return `€${(cents / 100).toFixed(2)}`
}

const StaffConsumptionStep = ({ date, onNext, onBack, isFirst }: StaffConsumptionStepProps) => {
  const { hasPermission } = usePermissions()
  const canViewReport = hasPermission(PERMISSIONS.STAFF_MEAL_REPORT)
  const canViewFinancials = hasPermission(PERMISSIONS.COSTING_MARGIN_VIEW)
  const [confirmed, setConfirmed] = useState(false)

  useEffect(() => {
    setConfirmed(false)
  }, [date])

  const { data, loading, error } = useQuery(GET_STAFF_CONSUMPTION_REPORT, {
    variables: { start: date, end: date },
    skip: !canViewReport,
    fetchPolicy: 'network-only',
  })

  const report = data?.staffConsumptionReport
  const rows: any[] = report?.rows ?? []
  const hasData = rows.length > 0

  // No permission — let the EOD person skip
  if (!canViewReport) {
    return (
      <div className="space-y-6">
        <div>
          <h2 className="text-lg font-semibold text-gray-900">Staff consumption</h2>
          <p className="mt-1 text-sm text-gray-500">Review staff meals eaten today.</p>
        </div>

        <div className="rounded-lg border border-gray-200 bg-gray-50 px-6 py-8 text-center">
          <LockClosedIcon className="mx-auto h-8 w-8 text-gray-300 mb-2" />
          <p className="text-sm font-medium text-gray-500">Staff meal report not available</p>
          <p className="mt-1 text-xs text-gray-400">
            Your role does not have access to the staff consumption report. Ask an owner or system admin to review it.
          </p>
        </div>

        <div className="flex items-center justify-between pt-2 border-t border-gray-100">
          <button
            onClick={onBack}
            disabled={isFirst}
            className="inline-flex items-center gap-1.5 rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-30"
          >
            <ArrowLeftIcon className="h-4 w-4" />
            Back
          </button>
          <button
            onClick={onNext}
            className="inline-flex items-center gap-1.5 rounded-md bg-primary-600 px-4 py-2 text-sm font-semibold text-white hover:bg-primary-500"
          >
            Next
            <ArrowRightIcon className="h-4 w-4" />
          </button>
        </div>
      </div>
    )
  }

  if (loading) {
    return (
      <div className="space-y-6">
        <div>
          <h2 className="text-lg font-semibold text-gray-900">Staff consumption</h2>
          <p className="mt-1 text-sm text-gray-500">Review staff meals eaten today.</p>
        </div>
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600" />
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="space-y-6">
        <div>
          <h2 className="text-lg font-semibold text-gray-900">Staff consumption</h2>
          <p className="mt-1 text-sm text-gray-500">Review staff meals eaten today.</p>
        </div>
        <div className="rounded-md bg-red-50 border border-red-200 p-4 text-sm text-red-700">
          Failed to load staff meal report: {error.message}
        </div>
        <div className="flex items-center justify-between pt-2 border-t border-gray-100">
          <button
            onClick={onBack}
            disabled={isFirst}
            className="inline-flex items-center gap-1.5 rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-30"
          >
            <ArrowLeftIcon className="h-4 w-4" />
            Back
          </button>
          <button
            onClick={onNext}
            className="inline-flex items-center gap-1.5 rounded-md border border-gray-200 px-4 py-2 text-sm font-medium text-gray-500 hover:bg-gray-50"
          >
            <ForwardIcon className="h-4 w-4" />
            Skip
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-lg font-semibold text-gray-900">Staff consumption</h2>
        <p className="mt-1 text-sm text-gray-500">
          Review all staff meals eaten today and confirm the list is complete before continuing.
        </p>
      </div>

      {/* No meals today */}
      {!hasData && (
        <div className="rounded-lg border border-dashed border-gray-200 bg-gray-50 px-6 py-10 text-center">
          <UserGroupIcon className="mx-auto h-8 w-8 text-gray-300 mb-2" />
          <p className="text-sm font-medium text-gray-400">No staff meals recorded today</p>
          <p className="mt-1 text-xs text-gray-400">No orders with a consuming employee were found for {date}.</p>
        </div>
      )}

      {/* Meal data per employee */}
      {hasData && (
        <div className="space-y-4">
          {/* Totals banner */}
          <div className="flex items-center justify-between rounded-lg bg-orange-50 border border-orange-200 px-4 py-3">
            <div className="text-sm text-orange-800">
              <span className="font-semibold">{report.total_orders}</span>{' '}
              {report.total_orders === 1 ? 'meal' : 'meals'} eaten by{' '}
              <span className="font-semibold">{rows.length}</span>{' '}
              {rows.length === 1 ? 'employee' : 'employees'}
            </div>
            {canViewFinancials && (
              <div className="text-sm font-semibold text-orange-800">
                Total value: {formatEuro(report.total_value_cents)}
              </div>
            )}
          </div>

          {/* Per-employee breakdown */}
          {rows.map((row: any) => (
            <div key={row.employee.id} className="rounded-lg border border-gray-200 overflow-hidden">
              {/* Employee header */}
              <div className="flex items-center justify-between bg-gray-50 px-4 py-3 border-b border-gray-200">
                <div className="flex items-center gap-2">
                  <UserGroupIcon className="h-4 w-4 text-gray-400" />
                  <span className="text-sm font-semibold text-gray-900">{row.employee.name}</span>
                  <span className="text-xs text-gray-500">
                    — {row.order_count} {row.order_count === 1 ? 'order' : 'orders'}
                  </span>
                </div>
                {canViewFinancials && (
                  <span className="text-sm font-medium text-gray-700">{formatEuro(row.total_value_cents)}</span>
                )}
              </div>

              {/* Items */}
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-xs text-gray-500 border-b border-gray-100">
                    <th className="px-4 py-2 font-medium">Product</th>
                    <th className="px-4 py-2 font-medium">Variant</th>
                    <th className="px-4 py-2 font-medium text-right">Qty</th>
                    {canViewFinancials && <th className="px-4 py-2 font-medium text-right">Value</th>}
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-50">
                  {row.items.map((item: any, idx: number) => (
                    <tr key={idx} className="hover:bg-gray-50">
                      <td className="px-4 py-2 text-gray-900">{item.product_name}</td>
                      <td className="px-4 py-2 text-gray-600">{item.variant_name}</td>
                      <td className="px-4 py-2 text-right text-gray-900">{item.quantity}</td>
                      {canViewFinancials && <td className="px-4 py-2 text-right text-gray-600">{formatEuro(item.value_cents)}</td>}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ))}

          {/* Confirmation checkbox */}
          <label className="flex items-start gap-3 cursor-pointer rounded-lg border-2 border-dashed border-gray-300 p-4 hover:border-primary-400 hover:bg-primary-50 transition-colors">
            <input
              type="checkbox"
              checked={confirmed}
              onChange={(e) => setConfirmed(e.target.checked)}
              className="mt-0.5 h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
            />
            <div>
              <p className="text-sm font-medium text-gray-900">
                I confirm this is a complete overview of all staff meals consumed today
              </p>
              <p className="mt-0.5 text-xs text-gray-500">
                Tick this box to continue. Any meals not listed here should have been registered via the POS.
              </p>
            </div>
          </label>
        </div>
      )}

      {/* Navigation */}
      <div className="flex items-center justify-between pt-2 border-t border-gray-100">
        <button
          onClick={onBack}
          disabled={isFirst}
          className="inline-flex items-center gap-1.5 rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-30"
        >
          <ArrowLeftIcon className="h-4 w-4" />
          Back
        </button>

        <div className="flex items-center gap-3">
          {/* "Nothing to log" only shown when there really are no meals */}
          {!hasData && (
            <button
              onClick={onNext}
              className="inline-flex items-center gap-1.5 rounded-md border border-gray-200 px-4 py-2 text-sm font-medium text-gray-500 hover:bg-gray-50"
            >
              <ForwardIcon className="h-4 w-4" />
              Nothing to log
            </button>
          )}

          <button
            onClick={onNext}
            disabled={hasData && !confirmed}
            className="inline-flex items-center gap-1.5 rounded-md bg-primary-600 px-4 py-2 text-sm font-semibold text-white hover:bg-primary-500 disabled:opacity-40 disabled:cursor-not-allowed"
          >
            {hasData && confirmed ? (
              <>
                <CheckCircleIcon className="h-4 w-4" />
                Confirmed — Next
              </>
            ) : (
              <>
                Next
                <ArrowRightIcon className="h-4 w-4" />
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  )
}

export default StaffConsumptionStep
